<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import V4Button from 'next/button/Button.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import CustomDashboardForm from '../components/CustomDashboardForm.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const uiFlags = useMapGetter('wgptCustomDashboards/getUIFlags');
const isSubmitting = computed(() => uiFlags.value.isCreating);
const formRef = ref(null);

async function handleSubmit(payload) {
  try {
    const created = await store.dispatch(
      'wgptCustomDashboards/create',
      payload
    );
    useAlert(t('WGPT_CUSTOM_DASHBOARDS.FORM.CREATE_SUCCESS'));
    router.push(
      accountScopedRoute('wgpt_reports_custom_dashboard_show', {
        dashboardId: created.id,
      })
    );
  } catch (error) {
    useAlert(error?.message || t('WGPT_CUSTOM_DASHBOARDS.FORM.CREATE_ERROR'));
  }
}

function handleCancel() {
  router.push(accountScopedRoute('wgpt_settings_custom_dashboards_list'));
}
</script>

<template>
  <section class="flex flex-col h-full w-full overflow-hidden bg-n-surface-1">
    <div class="border-b border-solid border-n-weak/60 shrink-0">
      <div class="max-w-7xl w-full mx-auto pt-4 pb-4 px-6">
        <BackButton compact />
        <div class="flex items-center justify-between gap-5 mt-2">
          <h1 class="text-2xl text-n-slate-12 font-medium mb-0 truncate">
            {{ t('WGPT_CUSTOM_DASHBOARDS.CREATE_TITLE') }}
          </h1>
          <div class="flex items-center gap-3 shrink-0">
            <V4Button
              type="button"
              variant="ghost"
              :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.CANCEL')"
              @click="handleCancel"
            />
            <V4Button
              type="button"
              :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.SAVE')"
              :is-loading="isSubmitting"
              :disabled="!formRef?.canSubmit || isSubmitting"
              @click="formRef?.submit()"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-auto px-6 py-6">
      <div class="max-w-7xl mx-auto pb-12">
        <CustomDashboardForm ref="formRef" @submit="handleSubmit" />
      </div>
    </div>
  </section>
</template>
